<template>
  <div class="filter-radio-list" role="radiogroup" :aria-label="ariaLabel || null">
    <button
      v-for="(opt, idx) in options"
      :key="opt.value"
      ref="opt"
      type="button"
      role="radio"
      :aria-checked="value === opt.value ? 'true' : 'false'"
      :tabindex="idx === activeIndex ? 0 : -1"
      class="filter-radio-list__opt"
      @click="select(opt.value)"
      @keydown="onKeydown($event, idx)"
      @focus="focusedIndex = idx"
    >
      <span class="filter-radio-list__radio" :class="{ 'is-on': value === opt.value }"></span>
      <span class="filter-radio-list__text">
        <span class="filter-radio-list__label" :class="{ 'is-selected': value === opt.value }">{{ opt.text }}</span>
        <span v-if="opt.subtitle" class="filter-radio-list__subtitle">{{ opt.subtitle }}</span>
      </span>
    </button>
  </div>
</template>

<script>
/**
 * Presentational single-select radio list for a filter popover. Exposed to assistive tech as an
 * ARIA radiogroup (role=radio + aria-checked per option) with roving-tabindex keyboard support.
 * Props in / events out: emits the picked value via `change`. With allowToggleOff (default),
 * clicking the already-selected option emits null so a chip can be un-set from its own list.
 */
export default {
  name: 'FilterRadioList',
  props: {
    // Each option: { value, text, subtitle? } — subtitle renders as a muted second line.
    options: { type: Array, required: true },
    // Currently-selected value (or null).
    value: { type: [String, Number, Boolean], default: null },
    allowToggleOff: { type: Boolean, default: true },
    // Labels the radiogroup for screen readers (the chip passes its dimension name).
    ariaLabel: { type: String, default: null },
  },
  data() {
    return { focusedIndex: -1 };
  },
  computed: {
    // Roving tabindex: exactly one option is tabbable — the last-focused one, else the selected
    // one, else the first — so Tab enters the group at the sensible spot.
    activeIndex() {
      if (this.focusedIndex >= 0 && this.focusedIndex < this.options.length) return this.focusedIndex;
      const sel = this.options.findIndex((o) => o.value === this.value);
      return sel >= 0 ? sel : 0;
    },
  },
  methods: {
    select(v) {
      this.$emit('change', this.allowToggleOff && this.value === v ? null : v);
    },
    // Arrow/Home/End move focus between options (roving tabindex). Selection stays on
    // click/Enter/Space (native <button>), which also closes the popover — so arrows must NOT
    // auto-select, or the first arrow would close the list.
    onKeydown(event, idx) {
      const n = this.options.length;
      if (!n) return;
      let next;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (idx + 1) % n;
      else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (idx - 1 + n) % n;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = n - 1;
      else return;
      event.preventDefault();
      this.focusedIndex = next;
      this.$nextTick(() => {
        const els = this.$refs.opt;
        if (els && els[next]) els[next].focus();
      });
    },
  },
};
</script>

<style lang="scss">
.filter-radio-list {
  &__opt {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 9px;
    border: none;
    background: none;
    border-radius: $border-radius-small;
    cursor: pointer;
    font-family: inherit;
    text-align: left;

    &:hover {
      background: $color-hover;
    }
  }

  &__radio {
    flex: none;
    width: 17px;
    height: 17px;
    border-radius: $radius-full;
    border: 1.5px solid $color-slate-300;
    display: grid;
    place-items: center;
    transition: border-color ease 100ms;

    &.is-on {
      border-color: $color-accent;

      &::after {
        content: '';
        width: 9px;
        height: 9px;
        border-radius: $radius-full;
        background: $color-accent;
      }
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-size: 13px;
    font-weight: normal;
    color: $color-text-body;

    // Emphasize the selected option — parity with the checkbox rows (FormCheckbox bolds checked
    // labels). A weight change is a non-color selection cue that aids scanning + accessibility.
    &.is-selected {
      font-weight: bold;
    }
  }

  // Muted second line on an option (e.g. "In the future" under "Anytime"), matching the mockup's
  // .popopt .osub: 11px / 600 / slate-400.
  &__subtitle {
    font-size: 11px;
    font-weight: 600;
    color: $color-slate-400;
    margin-top: 2px;
  }
}
</style>
